import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { Typography, Flex, RemoteImage } from '@banhala/ui-kit';
import type { IconItem } from '../types';
import type { AnyObject } from '../../../../../types';
import { bridge, isNotNil } from '../../../../../utils';
import {
  useBottomSheetRoute,
  useIsInFragmentScreens,
  useOpenDeeplink,
} from '../../../../../hooks';
import { useOverviewBottomSheet } from './OverviewBottomSheet';

interface ShortcutIconProps {
  testID?: string;
  item: IconItem;
  size: 'small' | 'medium';
  width: number;
  logging?: AnyObject;
  analyticsEventName?: string;
  onLogClick?: (logging: AnyObject) => void;
}

const isWebViewBottomSheetUrl = (url: string) => {
  return url.startsWith('ably://webview-bottomsheet');
};

const extractWebViewBottomSheetUrl = (deeplink: string): string | null => {
  const match = deeplink.match(/[?&]url=(.+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const ShortcutIcon = ({
  testID,
  item,
  size,
  width,
  logging,
  analyticsEventName,
  onLogClick,
}: ShortcutIconProps) => {
  const { name, representative_image, deeplink, badge, image_radius } = item;
  const { openDeeplink } = useOpenDeeplink();
  const { show: showOverviewBottomSheet } = useOverviewBottomSheet();
  const { open: openBottomSheetRoute } = useBottomSheetRoute();
  const isInFragmentScreens = useIsInFragmentScreens();

  const handlePress = useCallback(() => {
    if (deeplink) {
      if (onLogClick && logging) {
        onLogClick(logging);
      } else if (analyticsEventName && logging) {
        bridge.logAnalyticsEvent({
          name: analyticsEventName,
          analytics: logging.analytics,
          ably_only_log: logging.ably_only_log,
        });
      }

      if (isWebViewBottomSheetUrl(deeplink)) {
        const url = extractWebViewBottomSheetUrl(deeplink);
        if (isNotNil(url)) {
          if (isInFragmentScreens) {
            openBottomSheetRoute<{ url: string }, void>('overview', { url });
          } else {
            showOverviewBottomSheet({ url });
          }
          return;
        }
      }

      openDeeplink(deeplink);
    }
  }, [
    deeplink,
    onLogClick,
    logging,
    analyticsEventName,
    isInFragmentScreens,
    openBottomSheetRoute,
    openDeeplink,
    showOverviewBottomSheet,
  ]);

  if (size === 'small') {
    return (
      <Flex
        testID={testID}
        width={width}
        padding={{ top: 4 }}
        alignItems="center"
        onPress={handlePress}
      >
        <RemoteImage uri={representative_image} width={34} height={34} />
        <Flex padding={{ top: 6 }} width="100%">
          <Typography
            type="body5"
            color="content_primary"
            numberOfLines={1}
            textAlign="center"
          >
            {name}
          </Typography>
        </Flex>
        {badge?.image && (
          <View style={styles.smallBadge}>
            <RemoteImage uri={badge.image} width={28} height={14} />
          </View>
        )}
      </Flex>
    );
  }

  const borderRadius = image_radius === 'ROUNDED_RECTANGLE' ? 16 : 28;

  return (
    <Flex
      testID={testID}
      width={width}
      padding={{ top: 4 }}
      alignItems="center"
      onPress={handlePress}
    >
      <Flex width={56} height={56}>
        <Flex
          width={56}
          height={56}
          borderRadius={borderRadius}
          overflow={'hidden'}
        >
          <RemoteImage uri={representative_image} width={56} height={56} />
        </Flex>
        {badge?.image && (
          <Flex position={'absolute'} top={-2} right={-4}>
            <RemoteImage uri={badge.image} width={28} height={14} />
          </Flex>
        )}
      </Flex>
      <Flex padding={{ top: 6 }} width="100%">
        <Typography
          type="body5"
          color="content_primary"
          numberOfLines={2}
          textAlign="center"
        >
          {name}
        </Typography>
      </Flex>
    </Flex>
  );
};

const styles = StyleSheet.create({
  smallBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  mediumBadge: {
    position: 'absolute',
    top: -2,
    right: -3,
  },
});
